import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { UserProfile, ExamType } from '../../types/profile';
import { getMyProfile, saveMyProfile, getAllExams } from '../../components/apiClient.js';
import  ProfileNavigation  from './ProfileNavigation';


interface BasicInfoFormData {
    exam_type_id: number | null;
    current_level: string;
    current_score: number | null;
}

const BasicInfo: React.FC = () => {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [examTypes, setExamTypes] = useState<ExamType[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);

    // Initialize React Hook Form
    const {
        control,
        handleSubmit,
        watch,
        formState: { isValid },
        reset
    } = useForm<BasicInfoFormData>({
        defaultValues: {
            exam_type_id: null,
            current_level: '',
            current_score: null
        },
        mode: 'onChange'
    });

    // Watch form values for real-time updates
    const formData = watch();

    // Load profile data and exam types
    useEffect(() => {
        const loadData = async () => {
            try {
               // setLoading(true);

                // Load profile data
                const profileResponse = await getMyProfile();
                if (profileResponse.status === 'success' && profileResponse.result) {
                    const profileData = profileResponse.result;
                    setProfile(profileData);

                    // Reset form with profile data
                    reset({
                        exam_type_id: profileData.exam_type_id || null,
                        current_level: profileData.current_level || '',
                        current_score: profileData.current_score || null
                    });
                }

                // Load exam types
                const examTypesResponse = await getAllExams();
                if (examTypesResponse.status === 'success' && examTypesResponse.result) {
                    setExamTypes(examTypesResponse.result || []);
                }



            } catch (error) {
                console.error('Failed to load profile data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [reset]);

    const getMaxScore = useCallback(() => {
        if (!formData.exam_type_id) return 360;// Default max score if no exam selected
        const selectedExam = examTypes.find(exam => exam.id === formData.exam_type_id || exam.id === parseInt(String(formData.exam_type_id)));
        if (!selectedExam) return 360;
        return selectedExam.max_score || 360;
        
    }, [formData.exam_type_id, examTypes]);

    const getExamName = useCallback(() => {
        if (!formData.exam_type_id) return 'Exam';
        const selectedExam = examTypes.find(exam => exam.id === formData.exam_type_id || exam.id === parseInt(String(formData.exam_type_id)));
        return selectedExam ? selectedExam.exam_name : 'Exam';
    }, [formData.exam_type_id, examTypes]);

    // Form submission handler
    const onSubmit = async (data: BasicInfoFormData) => {
        if (isSaving) return;

        try {
            setIsSaving(true);

            // Merge form data with existing profile to prevent data loss
            const updatedProfile = {
                ...profile,
                ...data
            };

            const response = await saveMyProfile(updatedProfile);

            if (response.status === 'success') {
                setLastSaved(new Date());

                // Update local profile state
                setProfile(updatedProfile as UserProfile);
            } else {
                throw new Error(response.message || 'Failed to save profile');
            }
        } catch (error) {
            console.error('Save failed:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const currentLevelOptions = [
        {
            value: 'beginner',
            label: 'BEGINNER',
            description: 'Just starting preparation',
            icon: 'fa-seedling',
            color: 'success'
        },
        {
            value: 'exam-attempted',
            label: 'EXAM ATTEMPTED - NOT QUALIFIED',
            description: 'Previously attempted but did not qualify',
            icon: 'fa-redo-alt',
            color: 'warning'
        },
        {
            value: 'exam-qualified',
            label: 'EXAM QUALIFIED - LOW SCORE',
            description: 'Qualified with low score, aiming for improvement',
            icon: 'fa-chart-line',
            color: 'info'
        }
    ];

    if (loading) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading profile data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '10px' }}>
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-md-10 col-lg-12">
                        <ProfileNavigation className="mb-4" />
                        <div className="card shadow-sm border-0" style={{ borderRadius: '15px' }}>
                            <div className="card-header bg-gradient bg-primary text-white" style={{ borderRadius: '15px 15px 0 0' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0 fw-bold">
                                        <i className="fas fa-user-graduate me-2"></i>
                                        Basic Information
                                    </h4>
                                    {lastSaved && (
                                        <small className="text-white-50">
                                            <i className="fas fa-check me-1"></i>
                                            Saved {lastSaved.toLocaleTimeString()}
                                        </small>
                                    )}
                                </div>
                            </div>

                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="row g-4">
                                        {/* Exam Type Selection */}
                                        <div className="col-12">
                                            <label className="form-label fw-medium">
                                                Target Exam <span className="text-danger">*</span>
                                            </label>
                                            <Controller
                                                name="exam_type_id"
                                                control={control}
                                                rules={{ required: 'Please select an exam type' }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <select
                                                            {...field}
                                                            className={`form-select form-select-lg ${fieldState.error ? 'is-invalid' : ''}`}
                                                            value={field.value || ''}
                                                            onChange={(e) => field.onChange(parseInt(e.target.value) || null)}
                                                            style={{ borderRadius: '12px' }}
                                                        >
                                                            <option value="">Select your target exam</option>
                                                            {examTypes.map(exam => (
                                                                <option key={exam.id} value={exam.id}>
                                                                    {exam.exam_name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {fieldState.error && (
                                                            <div className="invalid-feedback">{fieldState.error.message}</div>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>

                                        {/* Current Level */}
                                        <div className="col-12">
                                            <label className="form-label fw-medium mb-3">
                                                Current Preparation Level <span className="text-danger">*</span>
                                            </label>
                                            <Controller
                                                name="current_level"
                                                control={control}
                                                rules={{ required: 'Please select your current level' }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        {fieldState.error && (
                                                            <div className="alert alert-danger mb-3">
                                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                                {fieldState.error.message}
                                                            </div>
                                                        )}
                                                        <div className="row g-3">
                                                            {currentLevelOptions.map((level) => (
                                                                <div key={level.value} className="col-12">
                                                                    <div
                                                                        className={`card border-2 cursor-pointer position-relative ${field.value === level.value
                                                                            ? `border-${level.color} bg-light shadow-sm`
                                                                            : 'border-light'
                                                                            }`}
                                                                        onClick={() => field.onChange(level.value)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        <div className="card-body p-3">
                                                                            {field.value === level.value && (
                                                                                <div className="position-absolute top-0 end-0 m-2">
                                                                                    <div className={`bg-${level.color} text-white rounded-circle d-flex align-items-center justify-content-center`}
                                                                                        style={{ width: '20px', height: '20px' }}>
                                                                                        <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            <div className="row align-items-center">
                                                                                <div className="col-auto">
                                                                                    <div className={`rounded-circle d-flex align-items-center justify-content-center ${field.value === level.value
                                                                                        ? `bg-${level.color} text-white`
                                                                                        : `bg-${level.color} bg-opacity-20 text-${level.color}`
                                                                                        }`} style={{ width: '45px', height: '45px' }}>
                                                                                        <i className={`fas ${level.icon}`}></i>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="col">
                                                                                    <h6 className={`mb-1 fw-bold ${field.value === level.value ? `text-${level.color}` : 'text-dark'
                                                                                        }`}>
                                                                                        {level.label}
                                                                                    </h6>
                                                                                    <p className={`mb-0 small ${field.value === level.value ? 'text-dark' : 'text-muted'
                                                                                        }`}>
                                                                                        {level.description}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            />
                                        </div>

                                        {/* Score Input Section - Only show for attempted/qualified levels */}
                                        {(formData.current_level === 'exam-attempted' || formData.current_level === 'exam-qualified') && (
                                            <div className="col-12">
                                                <div className="card border-info border-2">
                                                    <div className="card-header bg-info text-white">
                                                        <h6 className="mb-0">
                                                            <i className="fas fa-calculator me-2"></i>
                                                            Enter Your Current Score
                                                        </h6>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row align-items-center">
                                                            <div className="col-md-6">
                                                                <label className="form-label fw-medium">
                                                                    {formData.current_level === 'exam-attempted' ? 'Previous Attempt Score' : 'Your Current Score'}
                                                                </label>
                                                                <Controller
                                                                    name="current_score"
                                                                    control={control}
                                                                    rules={{
                                                                        required: 'Please enter your score for this level',
                                                                        min: { value: 0, message: 'Score must be positive' },
                                                                        max: { value: getMaxScore(), message: `Score must not exceed ${getMaxScore()}` }
                                                                    }}
                                                                    render={({ field, fieldState }) => (
                                                                        <>
                                                                            <div className="input-group input-group-lg">
                                                                                <input
                                                                                    {...field}
                                                                                    type="number"
                                                                                    className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                                                                    value={field.value || ''}
                                                                                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                                                                                    placeholder="Enter your score"
                                                                                    min="0"
                                                                                    max={getMaxScore()}
                                                                                />
                                                                                <span className="input-group-text">/ {getMaxScore()}</span>
                                                                            </div>
                                                                            {fieldState.error && (
                                                                                <div className="invalid-feedback d-block">{fieldState.error.message}</div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                />
                                                                <small className="text-muted">
                                                                    Enter your {getExamName()} score from your {formData.current_level === 'exam-attempted' ? 'previous attempt' : 'last exam'}
                                                                </small>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <div className="bg-light p-3 rounded">
                                                                    <h6 className="text-primary mb-2">
                                                                        <i className="fas fa-info-circle me-2"></i>
                                                                        Score Guidelines
                                                                    </h6>
                                                                    <ul className="small mb-0">
                                                                        <li><strong>NEET:</strong> Max 720 marks</li>
                                                                        <li><strong>JEE Mains:</strong> Max 300 marks</li>
                                                                        <li><strong>JEE Advanced:</strong> Max 372 marks</li>
                                                                    </ul>
                                                                    <div className="mt-2">
                                                                        <small className="text-success">
                                                                            <i className="fas fa-info-circle me-1"></i>
                                                                            Current exam: <strong>{getExamName()}</strong> (Max: {getMaxScore()} marks)
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Progress Indicators */}
                                    <div className="mt-4">
                                        <div className="card bg-light border-0" style={{ borderRadius: '12px' }}>
                                            <div className="card-body p-3">
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <div className="d-flex align-items-center">
                                                            <div className={`rounded-circle me-2 ${formData.exam_type_id ? 'bg-success' : 'bg-secondary'}`}
                                                                style={{ width: '12px', height: '12px' }}></div>
                                                            <small>Exam Type Selected</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="d-flex align-items-center">
                                                            <div className={`rounded-circle me-2 ${formData.current_level ? 'bg-success' : 'bg-secondary'}`}
                                                                style={{ width: '12px', height: '12px' }}></div>
                                                            <small>Level Defined</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="d-flex align-items-center">
                                                            <div className={`rounded-circle me-2 ${formData.current_score ? 'bg-success' : 'bg-warning'}`}
                                                                style={{ width: '12px', height: '12px' }}></div>
                                                            <small>Score Added (Optional)</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Save Button */}
                                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                                        <div className="d-flex align-items-center">
                                            {lastSaved && (
                                                <small className="text-muted">
                                                    <i className="fas fa-check text-success me-1"></i>
                                                    Last saved: {lastSaved.toLocaleTimeString()}
                                                </small>
                                            )}
                                        </div>
                                        <button
                                            type="submit"
                                            className={`btn btn-primary btn-lg ${isSaving ? 'disabled' : !isValid ? 'disabled' : ''
                                                }`}
                                            disabled={isSaving || !isValid}
                                        >
                                            {isSaving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-save me-2"></i>
                                                    Save Changes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicInfo;